import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { ICcyConversionOrdersQuery } from "interfaces";
import { toIsoString, getPreviousBusinessDay } from "utils";
import { endOfDay } from "date-fns";

export type OrderIdField =
  | "order_id"
  | "reap_order_id"
  | "rio_order_id"
  | "bridge_order_id"
  | "capa_order_id"
  | "taza_order_id"
  | "openfx_order_id";

export interface AdminOrdersState {
  ccyConversionOrdersQuery: ICcyConversionOrdersQuery;
  old_filter?: ICcyConversionOrdersQuery;
  selectedOrderIdField: OrderIdField;
}

const defaultQuery: ICcyConversionOrdersQuery = {
  start_date: toIsoString(getPreviousBusinessDay(new Date())),
  end_date: toIsoString(endOfDay(new Date())),
  live_only: false,
  include_rate_quote: true,
};

const initialState: AdminOrdersState = {
  ccyConversionOrdersQuery: { ...defaultQuery },
  selectedOrderIdField: "order_id",
};

export const adminOrdersSlice = createSlice({
  name: "adminOrders",
  initialState,
  reducers: {
    setQuery: (
      state,
      {
        payload: { ...rest },
      }: PayloadAction<Partial<ICcyConversionOrdersQuery>>,
    ) => {
      state.ccyConversionOrdersQuery = {
        ...state.ccyConversionOrdersQuery,
        ...rest,
      };
    },
    setOrderId: (
      state,
      action: PayloadAction<{
        field: OrderIdField;
        value: string;
      }>,
    ) => {
      if (action.payload.value && !state.old_filter) {
        state.old_filter = {
          ...state.ccyConversionOrdersQuery,
        };
      }
      state.ccyConversionOrdersQuery = {
        [action.payload.field]: action.payload.value,
      };
      if (action.payload.value) {
        state.selectedOrderIdField = action.payload.field;
      }
      if (!action.payload.value && state.old_filter) {
        state.ccyConversionOrdersQuery = {
          ...state.old_filter,
        };
        state.old_filter = undefined;
      }
    },
    resetFilters: (state) => {
      state.ccyConversionOrdersQuery = defaultQuery;
      state.selectedOrderIdField = "order_id";
    },
    setSelectedOrderIdField: (state, action: PayloadAction<OrderIdField>) => {
      const currentField = state.selectedOrderIdField;
      const currentValue =
        state.ccyConversionOrdersQuery[
          currentField as keyof ICcyConversionOrdersQuery
        ];

      if (currentValue && currentField !== action.payload) {
        state.ccyConversionOrdersQuery = {
          [action.payload]: currentValue,
        };
      }
      state.selectedOrderIdField = action.payload;
    },
  },
});

export const { setQuery, setOrderId, resetFilters, setSelectedOrderIdField } =
  adminOrdersSlice.actions;

export default adminOrdersSlice.reducer;
